<script setup lang="ts">
import { Link, usePage } from '@inertiajs/vue3';
import { Menu, X } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import { useI18n } from '@/composables/useI18n';
import { dashboard, login, register } from '@/routes';
import { pricing } from '@/routes/public';
import { index as apiIndex } from '@/routes/public/api';
import { index as productIndex } from '@/routes/public/product';
import { index as resourcesIndex } from '@/routes/public/resources';

const mobileOpen = ref(false);
const isAuthenticated = computed(() => !!usePage().props.auth?.user);
const { __ } = useI18n();

const navItems = computed(() => [
    { label: __('public.product'), key: 'product', href: productIndex().url },
    { label: __('public.api'), key: 'api', href: apiIndex().url },
    { label: __('public.nav_pricing'), key: 'pricing', href: pricing().url },
    {
        label: __('public.resources'),
        key: 'resources',
        href: resourcesIndex().url,
    },
]);
</script>

<template>
    <header
        class="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-sm"
    >
        <div
            class="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8"
        >
            <Link
                href="/"
                class="flex shrink-0 items-center gap-2.5 font-semibold text-foreground"
            >
                <img
                    src="/logo/light.png"
                    alt="KwikTalk"
                    class="block h-8 w-auto dark:hidden"
                />
                <img
                    src="/logo/dark.png"
                    alt="KwikTalk"
                    class="hidden h-8 w-auto dark:block"
                />
            </Link>

            <nav class="hidden items-center gap-1 lg:flex">
                <Link
                    v-for="item in navItems"
                    :key="item.key"
                    :href="item.href"
                    class="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                    {{ item.label }}
                </Link>
            </nav>

            <div class="hidden items-center gap-2 lg:flex">
                <LanguageSwitcher />
                <template v-if="isAuthenticated">
                    <Link
                        :href="dashboard()"
                        class="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
                    >
                        {{ __('public.open_kwiktalk') }}
                    </Link>
                </template>
                <template v-else>
                    <Link
                        :href="login()"
                        class="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                        {{ __('public.login') }}
                    </Link>
                    <Link
                        :href="register()"
                        class="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
                    >
                        {{ __('public.start_free') }}
                    </Link>
                </template>
            </div>

            <button
                class="inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground lg:hidden"
                :aria-label="__('public.navigation_menu')"
                @click="mobileOpen = !mobileOpen"
            >
                <Menu v-if="!mobileOpen" class="size-6" />
                <X v-else class="size-6" />
            </button>
        </div>

        <div
            v-show="mobileOpen"
            class="border-t border-border bg-background lg:hidden"
        >
            <div class="space-y-1 px-4 py-4">
                <Link
                    v-for="item in navItems"
                    :key="item.key"
                    :href="item.href"
                    class="block rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                    @click="mobileOpen = false"
                >
                    {{ item.label }}
                </Link>
                <div class="flex items-center gap-2 pt-2">
                    <LanguageSwitcher />
                </div>
            </div>
            <div class="border-t border-border px-4 py-4">
                <template v-if="isAuthenticated">
                    <Link
                        :href="dashboard()"
                        class="flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
                        @click="mobileOpen = false"
                    >
                        {{ __('public.open_kwiktalk') }}
                    </Link>
                </template>
                <template v-else>
                    <Link
                        :href="login()"
                        class="flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                        @click="mobileOpen = false"
                    >
                        {{ __('public.login') }}
                    </Link>
                    <Link
                        :href="register()"
                        class="mt-2 flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
                        @click="mobileOpen = false"
                    >
                        {{ __('public.start_free') }}
                    </Link>
                </template>
            </div>
        </div>
    </header>
</template>
